'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useWishlist } from '@/context/WishlistContext';
import { HeartIcon } from './Icons';

export interface CardProduct {
  id: number;
  slug: string;
  name: string;
  price: number;
  mrp: number | null;
  image: string | null;
  hoverImage: string | null;
  categoryName: string | null;
  badge: string | null;
  fabric: string | null;
  inStock: boolean;
}

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);

export default function ProductCard({ product, priority = false }: { product: CardProduct; priority?: boolean }) {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const saved = isInWishlist(product.id);
  const off = product.mrp ? Math.round((1 - product.price / product.mrp) * 100) : 0;
  const href = `/products/${product.slug}`;

  return (
    <article className="group relative flex flex-col">
      <div className="al-tile relative aspect-[4/5] w-full">
        {product.image ? (
          <Image
            src={product.image}
            alt={product.name}
            fill
            priority={priority}
            sizes="(min-width:1536px) 20vw, (min-width:1024px) 25vw, (min-width:640px) 33vw, 50vw"
            className={`object-cover transition-all duration-700 group-hover:scale-[1.03] ${product.hoverImage ? 'group-hover:opacity-0' : ''}`}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-al-maroon to-al-ink" />
        )}
        {product.hoverImage && (
          <Image
            src={product.hoverImage}
            alt=""
            fill
            sizes="(min-width:1536px) 20vw, (min-width:1024px) 25vw, (min-width:640px) 33vw, 50vw"
            className="object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
          />
        )}

        <div className="absolute left-3 top-3 z-10 flex flex-col gap-1.5">
          {!product.inStock ? (
            <span className="bg-al-ink/80 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-al-cream backdrop-blur-sm">Sold out</span>
          ) : product.badge ? (
            <span className="border border-al-gold/60 bg-al-ink/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-al-gold backdrop-blur-sm">
              {product.badge}
            </span>
          ) : null}
        </div>
        {off >= 5 && (
          <span className="absolute bottom-3 left-3 z-10 bg-al-rose px-2 py-0.5 text-[10px] font-semibold tracking-wider text-al-ink">
            {off}% OFF
          </span>
        )}

        <button
          type="button"
          onClick={() =>
            toggleWishlist({
              product_id: product.id,
              name: product.name,
              slug: product.slug,
              image: product.image ?? undefined,
              base_price: product.price,
              category_name: product.categoryName ?? undefined,
            })
          }
          className={`absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-al-ink/60 backdrop-blur-sm transition-all hover:text-al-gold ${
            saved ? 'text-al-gold opacity-100' : 'text-al-cream opacity-100 lg:opacity-0 lg:group-hover:opacity-100'
          }`}
          aria-label={saved ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`}
          aria-pressed={saved}
        >
          <HeartIcon size={17} filled={saved} />
        </button>
      </div>

      <div className="mt-4 flex flex-1 flex-col">
        {(product.categoryName || product.fabric) && (
          <p className="text-[10px] uppercase tracking-[0.22em] text-al-cream/50">{product.fabric || product.categoryName}</p>
        )}
        <h3 className="mt-1.5 font-al-display text-[19px] leading-snug text-al-cream transition-colors group-hover:text-al-gold lg:text-[20px]">
          <Link href={href} className="al-link-cover">{product.name}</Link>
        </h3>
        <p className="mt-2 flex items-baseline gap-2 tabular-nums">
          <span className="text-[15px] font-medium text-al-cream">{usd(product.price)}</span>
          {product.mrp && <span className="text-[13px] text-al-cream/40 line-through">{usd(product.mrp)}</span>}
        </p>
      </div>
    </article>
  );
}
